// React
import React, { useState } from 'react';

// Assets
import { DeleteFilled } from '@ant-design/icons';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

// Components
import IconButton from 'components/@extended/IconButton';
import ChangeEmailModal from './ChangeEmailModal';

// Packages
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Collapse,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    InputAdornment,
    InputLabel,
    Paper,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';

import { Formik } from 'formik';
import * as Yup from 'yup';
import 'yup-phone-lite';

// Styles
import 'react-widgets/scss/styles.scss';

// Utils
import { errorMessage, nameRegExp, userRoles, userTypes } from 'utils/constants';
import OrgPropertiesService from '../../services/org-properties.service';
import OrgService from '../../services/org.service';
import ConfirmModal, { ConfirmModalType } from '../common/ConfirmModal';
import SnackbarNotify from '../../utils/snackbar/SnackbarNotify';
import capitalize from '../../utils/capitalize';
import { Loader } from '../index';
import CountryCodeSelect from '../common/CountryCodeSelect';
import { openCompactModal } from '../../utils/modal/modal.helper';
import { hasModules } from '../../utils/org-modules';
import { moduleKeys, OrgPermissions } from '../../utils/permissions';
import { usePermissions } from '../../hooks/usePermissions';
import MemberViolationsSection from '../../sections/guard-reports/MemberViolationsSection';

const CreateUpdateMemberModal = ({
    onClose,
    selectedMember,
    property,
    type,
    orgId,
    activeOrg,
    onTransferMember
}) => {
    const [showCallsLockInfo, setShowCallsLockInfo] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const { hasPermission } = usePermissions();
    const guestsEnabled = hasModules(activeOrg, moduleKeys.GUESTS);
    const violationsEnabled =
        type === 'edit' &&
        Boolean(selectedMember?.uid) &&
        hasModules(activeOrg, moduleKeys.GUARD) &&
        hasPermission(OrgPermissions.MANAGE_REPORTS);
    const buildInvitePermission = values => guestsEnabled && !values.invite;
    const buildPermissions = values => {
        const permissions = [];
        if (buildInvitePermission(values)) permissions.push('invite');
        if (!values.calls) permissions.push('calls');
        return permissions;
    };

    const [confirmPayload, setConfirmPayload] = useState({
        uid: '',
        first_name: '',
        last_name: '',
        email: '',
        propertyId: '',
        phone_number: ''
    });

    const closeConfirm = () => setConfirmOpen(false);

    const buildPhoneNumber = (code, phoneRaw) => {
        if (!phoneRaw) return '';
        return `${code}${phoneRaw}`;
    };

    const getExistingUserPayloadEmail = values => {
        const formEmail = values.email?.trim().toLowerCase();
        if (formEmail) return formEmail;

        const existingUserEmail = confirmPayload.email?.trim().toLowerCase();
        return existingUserEmail || null;
    };

    const handleDelete = async () => {
        try {
            setSubmitting(true);
            await OrgPropertiesService.removeUserAccess(
                orgId,
                property.id,
                selectedMember.uid
            );
            SnackbarNotify.success('Occupant removed successfully');
            onClose();
        } catch (err) {
            console.error('Error while removing occupant:', err);
            SnackbarNotify.error(`${err?.message || 'Unexpected error'}`);
        } finally {
            setSubmitting(false);
            setDeleteConfirmOpen(false);
        }
    };

    const handleAddExistingUser = async values => {
        try {
            setSubmitting(true);

            const email = getExistingUserPayloadEmail(values);

            const inviteData = {
                first_name: values.first_name.trim().toLowerCase(),
                last_name: values.last_name.trim().toLowerCase(),
                email,
                phone: {
                    number: Number.parseInt(values.phone),
                    code: Number.parseInt(values.code),
                    country: values.country
                },
                role: userRoles.RESIDENT,
                type: userTypes.MEMBER,
                primary: values.primary,
                minor: values.minor,
                permissions: buildPermissions(values)
            };

            await OrgPropertiesService.addExistingUser(
                orgId,
                property.id,
                confirmPayload.uid,
                inviteData
            );

            SnackbarNotify.success('Invite sent — pending acceptance.');
            closeConfirm();
            onClose();
        } catch (err) {
            console.error('Error while adding existing occupant:', err);
            SnackbarNotify.error(`${err?.message || 'Unexpected error'}`);
        } finally {
            setSubmitting(false);
        }
    };

    const submit = async values => {
        const isEdit = type === 'edit';
        if (isEdit) {
            try {
                setSubmitting(true);

                const userData = {
                    first_name: values.first_name.toLowerCase().trim(),
                    last_name: values.last_name.toLowerCase().trim(),
                    phone: {
                        number: Number.parseInt(values.phone),
                        code: Number.parseInt(values.code),
                        country: values.country
                    },
                    type: userTypes.MEMBER,
                    primary: values.primary,
                    minor: values.minor,
                    suspended: Boolean(values.suspended),
                    id_verification_override: Boolean(values.id_verification_override),
                    permissions: {
                        calls: !values.calls,
                        invite: buildInvitePermission(values)
                    }
                };

                await OrgPropertiesService.updateUser(
                    orgId,
                    property.id,
                    selectedMember.uid,
                    userData
                );

                SnackbarNotify.success('Occupant updated successfully');
                onClose();
                return;
            } catch (err) {
                console.error('Error while updating occupant:', err);
                SnackbarNotify.error(`${err?.message || 'Unexpected error'}`);
            } finally {
                setSubmitting(false);
            }

            return;
        }

        try {
            setSubmitting(true);

            const email = values.email.trim().toLowerCase();
            const phone_number = buildPhoneNumber(values.code, values.phone);

            const checkInvitePayload = {};
            if (email) checkInvitePayload.email = email;
            if (phone_number) checkInvitePayload.phone_number = phone_number;

            await OrgPropertiesService.checkInviteExists(
                orgId,
                property.id,
                checkInvitePayload
            );

            const checkUserPayload = {};
            if (email) checkUserPayload.email = email;
            if (phone_number) checkUserPayload.phone_number = phone_number;

            const userExists = await OrgService.checkUserExists(orgId, checkUserPayload);

            if (userExists?.uid) {
                if (userExists.property_ids?.includes(property.id)) {
                    throw new Error(
                        'This user is already linked to the selected property.'
                    );
                }

                setConfirmPayload({
                    uid: userExists.uid,
                    first_name: userExists?.first_name || '',
                    last_name: userExists?.last_name || '',
                    email: userExists?.email || '',
                    propertyId: property.id,
                    phone_number: userExists?.phone_number || ''
                });

                setConfirmOpen(true);
                return;
            }

            const inviteData = {
                org_id: orgId,
                first_name: values.first_name.trim().toLowerCase(),
                last_name: values.last_name.trim().toLowerCase(),
                phone: {
                    number: Number.parseInt(values.phone),
                    code: Number.parseInt(values.code),
                    country: values.country
                },
                role: userRoles.RESIDENT,
                type: userTypes.MEMBER,
                permissions: [
                    ...(buildInvitePermission(values) ? ['invite'] : []),
                    ...(!values.calls ? ['calls'] : [])
                ],
                primary: values.primary,
                minor: values.minor
            };

            if (email) inviteData.email = email;

            await OrgPropertiesService.createUserInvite(orgId, property.id, inviteData);

            SnackbarNotify.success('Invite sent — pending acceptance.');
            onClose();
        } catch (err) {
            console.error('Error while creating occupant:', err);
            SnackbarNotify.error(`${err?.message || 'Unexpected error'}`);
        } finally {
            setSubmitting(false);
        }
    };

    const initialValues =
        type === 'edit'
            ? {
                  first_name: selectedMember?.first_name?.trim() || '',
                  last_name: selectedMember?.last_name?.trim() || '',
                  email: selectedMember?.email?.trim() || '',
                  phone: selectedMember?.phone?.number
                      ? `${selectedMember.phone.number}`
                      : '',
                  code: selectedMember?.phone?.code
                      ? `${selectedMember.phone.code}`
                      : '1',
                  country: selectedMember?.phone?.country
                      ? `${selectedMember.phone.country}`
                      : 'US',
                  id_verification_override:
                      selectedMember?.properties?.[property.id]
                          ?.id_verification_override ??
                      activeOrg.user_verification_required,
                  suspended: selectedMember?.properties?.[property.id]?.suspended,
                  calls: !selectedMember?.properties?.[property.id]?.permissions?.calls,
                  invite: !selectedMember?.properties?.[property.id]?.permissions?.invite,
                  type:
                      selectedMember?.properties?.[property.id]?.type || userTypes.MEMBER,
                  primary: selectedMember?.properties?.[property.id]?.primary || false,
                  minor: selectedMember?.properties?.[property.id]?.minor || false
              }
            : {
                  first_name: '',
                  last_name: '',
                  email: '',
                  phone: '',
                  code: '1',
                  country: 'US',
                  type: userTypes.MEMBER,
                  calls: false,
                  invite: false,
                  primary: true,
                  minor: false
              };

    const validationSchema = Yup.object().shape({
        first_name: Yup.string()
            .trim()
            .matches(nameRegExp.format, errorMessage.firstName.valid)
            .required(errorMessage.firstName.required),
        last_name: Yup.string()
            .trim()
            .matches(nameRegExp.format, errorMessage.lastName.valid)
            .required(errorMessage.lastName.required),
        email:
            type !== 'edit'
                ? Yup.string().email(errorMessage.email.valid)
                : Yup.string()
                      .trim()
                      .lowercase()
                      .email(errorMessage.email.valid)
                      .required(errorMessage.email.required),
        phone: Yup.string().trim().required(errorMessage.phone.required),
        country: Yup.string().trim(),
        code: Yup.string().trim(),
        suspended: Yup.boolean().notRequired(),
        id_verification_override: Yup.boolean().notRequired(),
        calls: Yup.boolean(),
        invite: Yup.boolean(),
        type: Yup.string()
    });

    const validatePhoneNumber = ({ phone, code, country }) => {
        const phoneSchema = Yup.string().phone(country);

        if (!phone) return {};

        try {
            phoneSchema.validateSync(`${code}${phone}`);
            return {};
        } catch {
            return { phone: 'Phone number needs to be valid' };
        }
    };

    const handleTransferMember = () => {
        onTransferMember();
    };

    return (
        <>
            {submitting && <Loader />}
            <ConfirmModal
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                onConfirm={handleDelete}
                title="Confirm Delete"
                text={
                    <>
                        Are you sure you want to delete occupant{' '}
                        <strong>
                            {capitalize(selectedMember?.first_name)}{' '}
                            {capitalize(selectedMember?.last_name)}
                        </strong>
                        ?
                    </>
                }
                confirmText="Yes, Delete"
                cancelText="Cancel"
                type={ConfirmModalType.ERROR}
            />

            <DialogTitle>
                {type === 'edit' ? 'Edit Occupant' : 'Add New Occupant'}
            </DialogTitle>
            <Divider />
            <Formik
                initialValues={initialValues}
                onSubmit={submit}
                validate={validatePhoneNumber}
                validationSchema={validationSchema}
                validateOnChange
                enableReinitialize
            >
                {({
                    handleSubmit,
                    handleChange,
                    touched,
                    setFieldValue,
                    handleBlur,
                    validateField,
                    errors,
                    values
                }) => {
                    const handleOpenChangeEmailModal = () => {
                        openCompactModal(
                            ChangeEmailModal,
                            {
                                currentEmail: values.email,
                                orgId,
                                propertyId: property?.id,
                                userId: selectedMember?.uid,
                                onSuccess: newEmail => {
                                    setFieldValue('email', newEmail);
                                }
                            },
                            { fullWidth: true }
                        );
                    };

                    const callsLockedByIdentity =
                        type === 'edit' && !selectedMember?.identity_verified;

                    return (
                        <>
                            <ConfirmModal
                                open={confirmOpen}
                                onClose={closeConfirm}
                                onConfirm={() => handleAddExistingUser(values)}
                                title="Existing Sage Member Found"
                                text={
                                    <>
                                        User{' '}
                                        <strong>
                                            {capitalize(confirmPayload.first_name)}{' '}
                                            {capitalize(confirmPayload.last_name)}
                                        </strong>{' '}
                                        already exists.
                                        <br />
                                        <strong>Email:</strong> {confirmPayload.email}
                                        <br />
                                        <strong>Phone:</strong> +
                                        {confirmPayload.phone_number}
                                        <br />
                                        <br />
                                        Send this user an invite to the property? They
                                        will need to accept it before gaining access.
                                    </>
                                }
                                confirmText="Send Invite"
                                cancelText="Cancel"
                                type={ConfirmModalType.WARNING}
                            />

                            <DialogContent sx={{ p: 2.5 }}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <Stack spacing={0.5}>
                                            <InputLabel>First Name</InputLabel>
                                            <TextField
                                                required
                                                id="firstNameBasic"
                                                name="first_name"
                                                placeholder="First Name"
                                                fullWidth
                                                autoComplete="given-name"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                error={
                                                    touched.first_name &&
                                                    !!errors.first_name
                                                }
                                                helperText={
                                                    touched.first_name &&
                                                    errors.first_name
                                                        ? errors.first_name
                                                        : ''
                                                }
                                                value={capitalize(values.first_name)}
                                            />
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Stack spacing={0.5}>
                                            <InputLabel>Last Name</InputLabel>
                                            <TextField
                                                required
                                                id="lastNameBasic"
                                                name="last_name"
                                                placeholder="Last name"
                                                fullWidth
                                                autoComplete="family-name"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                error={
                                                    touched.last_name &&
                                                    !!errors.last_name
                                                }
                                                helperText={
                                                    touched.last_name && errors.last_name
                                                        ? errors.last_name
                                                        : ''
                                                }
                                                value={capitalize(values.last_name)}
                                            />
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={12} sm={12}>
                                        <Stack spacing={0.5}>
                                            <InputLabel>Email</InputLabel>

                                            <TextField
                                                required={type === 'edit'}
                                                id="emailBasic"
                                                name="email"
                                                placeholder="Email"
                                                fullWidth
                                                autoComplete="email"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                error={touched.email && !!errors.email}
                                                helperText={
                                                    touched.email && errors.email
                                                        ? errors.email
                                                        : ''
                                                }
                                                value={values.email}
                                                disabled={type === 'edit'}
                                                InputProps={{
                                                    readOnly: type === 'edit',
                                                    endAdornment:
                                                        type === 'edit' ? (
                                                            <InputAdornment position="end">
                                                                <Button
                                                                    variant="text"
                                                                    size="small"
                                                                    onClick={
                                                                        handleOpenChangeEmailModal
                                                                    }
                                                                    sx={{
                                                                        whiteSpace:
                                                                            'nowrap'
                                                                    }}
                                                                >
                                                                    Change Email
                                                                </Button>
                                                            </InputAdornment>
                                                        ) : null
                                                }}
                                            />
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={2} sm={2}>
                                        <Stack spacing={0.5}>
                                            <InputLabel htmlFor="country">
                                                Country Code
                                            </InputLabel>
                                            <CountryCodeSelect
                                                value={selectedCountry}
                                                defaultRegion={values.country || 'US'}
                                                onChange={selectedCountry => {
                                                    if (!selectedCountry) return;

                                                    setSelectedCountry(selectedCountry);

                                                    setFieldValue(
                                                        'code',
                                                        String(
                                                            selectedCountry.code
                                                        ).replace(/\D/g, '')
                                                    );

                                                    setFieldValue(
                                                        'country',
                                                        selectedCountry.region
                                                    );

                                                    setTimeout(() => {
                                                        validateField('phone');
                                                    });
                                                }}
                                                label=""
                                                size="medium"
                                            />
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={10} sm={6}>
                                        <Stack spacing={0.5}>
                                            <InputLabel>Phone</InputLabel>
                                            <TextField
                                                required
                                                id="phoneBasic"
                                                name="phone"
                                                placeholder="Phone"
                                                inputMode="numeric"
                                                onChange={e =>
                                                    setFieldValue(
                                                        'phone',
                                                        e.target.value.replace(/\D/g, '')
                                                    )
                                                }
                                                fullWidth
                                                autoComplete="phone"
                                                onBlur={handleBlur}
                                                error={touched.phone && !!errors.phone}
                                                helperText={
                                                    touched.phone && errors.phone
                                                        ? errors.phone
                                                        : ''
                                                }
                                                value={values.phone}
                                            />
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={12} sm={12}>
                                        <Stack spacing={2}>
                                            <Stack direction="row" spacing={4}>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={values.primary}
                                                            onChange={(_, checked) => {
                                                                setFieldValue(
                                                                    'primary',
                                                                    checked
                                                                );

                                                                if (checked) {
                                                                    setFieldValue(
                                                                        'minor',
                                                                        false
                                                                    );
                                                                    setFieldValue(
                                                                        'calls',
                                                                        false
                                                                    );
                                                                    setFieldValue(
                                                                        'invite',
                                                                        false
                                                                    );
                                                                }
                                                            }}
                                                            disabled={values.minor}
                                                        />
                                                    }
                                                    label="Owner"
                                                />

                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={values.minor}
                                                            onChange={(_, checked) => {
                                                                setFieldValue(
                                                                    'minor',
                                                                    checked
                                                                );

                                                                if (checked) {
                                                                    setFieldValue(
                                                                        'primary',
                                                                        false
                                                                    );
                                                                    setFieldValue(
                                                                        'calls',
                                                                        true
                                                                    );
                                                                    setFieldValue(
                                                                        'invite',
                                                                        true
                                                                    );
                                                                    setFieldValue(
                                                                        'id_verification_override',
                                                                        false
                                                                    );
                                                                } else {
                                                                    setFieldValue(
                                                                        'calls',
                                                                        false
                                                                    );
                                                                    setFieldValue(
                                                                        'invite',
                                                                        false
                                                                    );
                                                                }
                                                            }}
                                                            disabled={values.primary}
                                                        />
                                                    }
                                                    label="Minor"
                                                />
                                            </Stack>
                                        </Stack>
                                    </Grid>

                                    {type === 'edit' && (
                                        <Grid item xs={12} sm={12}>
                                            <Stack spacing={0.5}>
                                                <Box>
                                                    {activeOrg.user_verification_required ===
                                                        true && (
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={
                                                                        values.id_verification_override
                                                                    }
                                                                    onChange={() =>
                                                                        setFieldValue(
                                                                            'id_verification_override',

                                                                            !values.id_verification_override
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        values.minor
                                                                    }
                                                                />
                                                            }
                                                            label={
                                                                <Typography
                                                                    sx={{
                                                                        color: '#8c8c8c'
                                                                    }}
                                                                >
                                                                    Required verification
                                                                </Typography>
                                                            }
                                                            labelPlacement="end"
                                                            sx={{ ml: 0 }}
                                                        />
                                                    )}
                                                    <FormControlLabel
                                                        value="end"
                                                        control={
                                                            <Checkbox
                                                                checked={values.suspended}
                                                                onChange={() =>
                                                                    setFieldValue(
                                                                        'suspended',
                                                                        !values.suspended
                                                                    )
                                                                }
                                                            />
                                                        }
                                                        label={
                                                            <Typography
                                                                sx={{
                                                                    color: '#8c8c8c'
                                                                }}
                                                            >
                                                                Suspended
                                                            </Typography>
                                                        }
                                                        labelPlacement="end"
                                                        sx={{ ml: 0 }}
                                                    />
                                                </Box>
                                            </Stack>
                                        </Grid>
                                    )}

                                    <Grid item xs={12} sm={12}>
                                        <Stack spacing={1}>
                                            <Box>
                                                <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                    spacing={1.5}
                                                    flexWrap="wrap"
                                                >
                                                    <FormControlLabel
                                                        value="end"
                                                        control={
                                                            <Checkbox
                                                                checked={values.calls}
                                                                onChange={() =>
                                                                    setFieldValue(
                                                                        'calls',
                                                                        !values.calls
                                                                    )
                                                                }
                                                                disabled={
                                                                    values.minor ||
                                                                    callsLockedByIdentity
                                                                }
                                                            />
                                                        }
                                                        label={
                                                            <Typography
                                                                sx={{
                                                                    color: '#8c8c8c'
                                                                }}
                                                            >
                                                                Hide from calls
                                                            </Typography>
                                                        }
                                                        labelPlacement="end"
                                                        sx={{ ml: 0, mr: 0 }}
                                                    />

                                                    {callsLockedByIdentity && (
                                                        <Chip
                                                            icon={
                                                                <LockOutlinedIcon
                                                                    sx={{
                                                                        fontSize: 16
                                                                    }}
                                                                />
                                                            }
                                                            label="Pending SAGE Identity"
                                                            clickable
                                                            onClick={() =>
                                                                setShowCallsLockInfo(
                                                                    prev => !prev
                                                                )
                                                            }
                                                            variant="outlined"
                                                            sx={{
                                                                height: 30,
                                                                borderRadius: '16px',
                                                                color: '#6b7280',
                                                                borderColor: '#d1d5db',
                                                                backgroundColor: '#fff',
                                                                '& .MuiChip-label': {
                                                                    px: 1,
                                                                    fontWeight: 600
                                                                },
                                                                '& .MuiChip-icon': {
                                                                    color: '#6b7280'
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                </Stack>
                                            </Box>
                                            {callsLockedByIdentity && (
                                                <Collapse in={showCallsLockInfo}>
                                                    <Paper
                                                        variant="outlined"
                                                        sx={{
                                                            p: 2,
                                                            borderRadius: 2,
                                                            borderColor: '#d9e2d9',
                                                            backgroundColor: '#fafaf7',
                                                            borderLeft:
                                                                '3px solid #06D386'
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                color: 'text.primary',
                                                                lineHeight: 1.5
                                                            }}
                                                        >
                                                            <Box
                                                                component="span"
                                                                sx={{
                                                                    fontWeight: 700
                                                                }}
                                                            >
                                                                Identity verification
                                                                required.
                                                            </Box>{' '}
                                                            This setting is managed by
                                                            SAGE Identity and will unlock
                                                            once the resident completes
                                                            their profile.
                                                        </Typography>
                                                    </Paper>
                                                </Collapse>
                                            )}
                                        </Stack>
                                    </Grid>
                                    {guestsEnabled && (
                                        <Grid item xs={12} sm={12}>
                                            <Stack spacing={0.5}>
                                                <Box>
                                                    <FormControlLabel
                                                        value="end"
                                                        control={
                                                            <Checkbox
                                                                checked={values.invite}
                                                                onChange={() =>
                                                                    setFieldValue(
                                                                        'invite',
                                                                        !values.invite
                                                                    )
                                                                }
                                                                disabled={values.minor}
                                                            />
                                                        }
                                                        label={
                                                            <Typography
                                                                sx={{ color: '#8c8c8c' }}
                                                            >
                                                                Disable guest invites
                                                            </Typography>
                                                        }
                                                        labelPlacement="end"
                                                        sx={{ ml: 0 }}
                                                    />
                                                </Box>
                                            </Stack>
                                        </Grid>
                                    )}
                                    {violationsEnabled && (
                                        <Grid item xs={12}>
                                            <Divider sx={{ mb: 2 }} />
                                            <Typography variant="subtitle1" gutterBottom>
                                                Violations
                                            </Typography>
                                            <MemberViolationsSection
                                                orgId={orgId}
                                                uid={selectedMember.uid}
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                            </DialogContent>

                            <Divider />

                            <DialogActions sx={{ p: 2.5 }}>
                                <Grid
                                    container
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <Grid item>
                                        {type === 'edit' && (
                                            <div>
                                                <Tooltip
                                                    title="Delete Occupant"
                                                    placement="top"
                                                >
                                                    <IconButton
                                                        size="large"
                                                        color="error"
                                                        onClick={() =>
                                                            setDeleteConfirmOpen(true)
                                                        }
                                                        disabled={submitting}
                                                    >
                                                        <DeleteFilled />
                                                    </IconButton>
                                                </Tooltip>

                                                <Button
                                                    type="button"
                                                    variant="contained"
                                                    color={'secondary'}
                                                    sx={{ ml: 1 }}
                                                    onClick={handleTransferMember}
                                                >
                                                    Transfer Occupant
                                                </Button>
                                            </div>
                                        )}
                                    </Grid>

                                    <Grid item>
                                        <Stack
                                            direction="row"
                                            spacing={2}
                                            alignItems="center"
                                        >
                                            <Button
                                                color="error"
                                                onClick={() => {
                                                    onClose();
                                                }}
                                            >
                                                Cancel
                                            </Button>

                                            <Button
                                                type="submit"
                                                variant="contained"
                                                disabled={submitting}
                                                onClick={handleSubmit}
                                                id="add_update_occupant_button"
                                            >
                                                {type === 'edit'
                                                    ? 'Update'
                                                    : submitting
                                                    ? 'Processing...'
                                                    : 'Add'}
                                            </Button>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </DialogActions>
                        </>
                    );
                }}
            </Formik>
        </>
    );
};

export default CreateUpdateMemberModal;
